import { useAfterFirstRender } from "@/app/hooks/useAfterFirstRender";
import { useFormUtils } from "@/app/hooks/useFormUtils";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import FormFieldErrors from "../../Reusable/FormFieldErrors";
import { ISelectMenuItemData, IServiceSubServiceSelectMenu } from "../../interface";

const ServiceSubServiceSelectMenu: React.FC<IServiceSubServiceSelectMenu> = ({
  onSelectItem,
  errorMessages,
  register,
  fieldError,
  showTypedErrors,
  itemsClassName,
  showDropdownWhenNoData,
  defaultSelected,
  noDataDropDownContent,
  listBoxClassName,
  showTooltips,
  // defaultSubItemsSelected,
  className,
  ...props
}) => {
  const { deduceFormFieldErrors, isFormFieldValid } = useFormUtils();
  const [selectedOptions, setSelectedOptions] = useState<Array<ISelectMenuItemData>>([]);
  const [selectedSubOptions, setSelectedSubOptions] = useState<{
    [key: string]: ISelectMenuItemData[];
  } | null>(null);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [query, setQuery] = useState("");

  const filteredItems =
    query === ""
      ? props?.selectItems
      : props?.selectItems.filter((item) =>
          item?.title?.toLowerCase()?.replace(/\s+/g, "")?.includes(query?.toLowerCase()?.replace(/\s+/g, "")),
        );

  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);
  const isValid: boolean = isFormFieldValid(reducedErrorMessages);

  const handleSelectItems = (items: ISelectMenuItemData[]) => {
    // const selectedItems = items
    //   .filter((v, i, a) => a.findIndex((v2) => v2.id === v.id) === i)
    //   .map((s) => {
    //     const { subSelectItems, ...rest } = s;
    //     return { ...rest, subSelectItems: [] as ISelectMenuItemData[] };
    //   });
    setSelectedOptions(items);
    if (onSelectItem) {
      onSelectItem(items);
    }
    items?.forEach((si) => {
      setSelectedSubOptions((prev) => {
        return {
          ...prev,
          [`${si.id}`]: prev?.[si.id] || si?.subSelectItems || [],
        };
      });
    });
  };

  /* Handles subitems selection */

  const renderDisplay = (items: ISelectMenuItemData[]) => {
    if (!items) return "";
    if (items?.length === 0) {
      return "";
    } else if (items?.length === 1) {
      return items[0]?.title;
    } else {
      let label = items[0]?.title ?? "";
      label = label?.length > 15 ? `${label.substring(0, 15)}..` : label;
      return `${label} , +${items.length - 1}`;
    }
  };

  const isChecked = (item: ISelectMenuItemData) => {
    return selectedOptions?.find((so) => so?.id === item?.id);
  };

  const isSubChecked = (itemId: string, subItem: ISelectMenuItemData) => {
    return selectedSubOptions?.[itemId]?.find((so) => so?.id === subItem?.id);
  };

  const renderEmtpyState = (cascadeMenuOpen: boolean) => {
    if (props?.watchCascadeSelectMenuState) {
      props?.watchCascadeSelectMenuState(cascadeMenuOpen);
    }
    return null;
  };

  const handleSelectSubItems = (itemId: string, subItem: ISelectMenuItemData) => {
    const isSubItemSelected = selectedSubOptions?.[itemId]?.filter((si) => si.id === subItem.id)?.length > 0;
    if (isSubItemSelected) {
      setSelectedSubOptions((prev) => {
        return { ...prev, [`${itemId}`]: prev[itemId]?.filter((prevSi) => prevSi.id !== subItem.id) };
      });
      if (props?.onSelectSubItem) {
        props?.onSelectSubItem({
          ...selectedSubOptions,
          [`${itemId}`]: selectedSubOptions[itemId]?.filter((prevSi) => prevSi.id !== subItem.id),
        });
      }
    } else {
      setSelectedSubOptions((prev) => ({
        ...prev,
        [`${itemId}`]: prev?.[itemId] ? [...prev[itemId], subItem] : [subItem],
      }));
      if (props?.onSelectSubItem) {
        props?.onSelectSubItem({
          ...selectedSubOptions,
          [`${itemId}`]: selectedSubOptions?.[itemId] ? [...selectedSubOptions[itemId], subItem] : [subItem],
        });
      }
    }
  };

  useAfterFirstRender(() => {
    if (selectAll) {
      handleSelectItems(filteredItems);
    } else {
      handleSelectItems([]);
    }
  }, [selectAll]);

  useEffect(() => {
    if (defaultSelected?.length > 0 && selectedOptions?.length == 0) {
      handleSelectItems(defaultSelected);
      // setSelectedOptions(defaultSelected);
      // defaultSelected.map((s) => handleSelectSubItems(s.id, s));
      // handleSelectSubItems(defaultSelected.);
    }
  }, [defaultSelected]);

  // useEffect(() => {
  //   if (defaultSubItemsSelected && Object.keys(defaultSubItemsSelected).length > 0) {
  //     setSelectedSubOptions(defaultSubItemsSelected);
  //   }
  // }, [defaultSubItemsSelected]);

  const showFieldErrors = showTypedErrors && showTypedErrors === true ? true : showTypedErrors === false ? false : true;

  return (
    <div style={props?.style ?? {}} className={clsx("relative", props?.containerClassName ?? "")}>
      <Combobox
        disabled={props?.disabled && props?.disabled === true ? true : false}
        value={selectedOptions}
        onChange={(items: ISelectMenuItemData[]) => {
          const countMap = items.reduce((acc, obj) => {
            acc[obj.id + obj.title] = (acc[obj.id + obj.title] || 0) + 1;
            return acc;
          }, {});
          const newitems = items
            .filter((obj) => countMap[obj.id + obj.title] === 1)
            .map((n) => {
              const { subSelectItems, ...rest } = n;
              return {
                ...rest,
                subSelectItems: selectedSubOptions?.[n.id] || [],
              };
            });
          setSelectedSubOptions((prev) => {
            const newkeys = Object.keys(prev || {})?.filter((p) => newitems?.some((n) => n.id == p));
            const subitems = {};
            for (const key in prev) {
              if (newkeys?.includes(key)) {
                subitems[key] = prev?.[key];
              }
            }
            return subitems;
          });
          handleSelectItems(newitems);
        }}
        multiple
        nullable
      >
        {({ open }) => {
          {
            renderEmtpyState(open);
          }
          return (
            <>
              {props?.label && (
                <span className="absolute -top-4 text-xs left-1 text-gray-500 z-10">{props?.label}</span>
              )}
              <div className="relative  w-full">
                {!props?.isCascaded ? (
                  <div
                    className={clsx(
                      "relative w-full cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white text-left  focus:outline-none focus-visible:ring-1 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm",
                      !isValid && "border-none ",
                      props?.disabled && "bg-gray-100",
                    )}
                  >
                    <Combobox.Button className={"w-full"}>
                      <Combobox.Input
                        className={clsx(
                          `w-full text-left text-xs font-medium  rounded-lg py-2 pl-3 pr-10 text-gray-500 leading-5  focus:ring-0 ${className}`,
                          isValid
                            ? "text-gray-500 border-none outline-none font-medium"
                            : "text-red-600  border border-red-500  outline-red-500 font-medium",
                          props?.disabled && props?.disabled === true && "bg-gray-200",
                        )}
                        displayValue={(items: ISelectMenuItemData[]) => {
                          return renderDisplay(items);
                        }}
                        autoComplete="off"
                        placeholder={props?.placeholder ?? "Select Multiple"}
                        onChange={(event) => setQuery(event.target.value)}
                      />
                    </Combobox.Button>
                    <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </Combobox.Button>
                  </div>
                ) : (
                  <div
                    className={clsx(
                      "relative  cursor-default overflow-hidden rounded-lg  bg-white text-left  focus:outline-none w-64 focus-visible:ring-1 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm",
                      !isValid && "border-none ",
                      props?.disabled && "bg-gray-100",
                    )}
                  ></div>
                )}
                {showFieldErrors && (
                  <div className="">
                    <FormFieldErrors errors={reducedErrorMessages} />
                  </div>
                )}
                <Transition
                  show={props?.cascadeState ? props?.cascadeState : open}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => setQuery("")}
                >
                  <Combobox.Options
                    style={{ zIndex: 1 }}
                    className={clsx(
                      "absolute  mt-1 w-full  overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none max-h-60 sm:text-sm",
                      listBoxClassName ? listBoxClassName : "",
                    )}
                  >
                    {props?.withSelectAll === true && (
                      <Combobox.Option
                        key={"SelectALlminus1"}
                        className={({ selected: _l }) =>
                          clsx(
                            "relative cursor-pointer flex font-medium pr-4 pl-2 text-xs justify-between items-center select-none py-2 text-center ",
                            selectAll
                              ? "bg-gray-100 hover:bg-gray-100 text-gray-800 font-medium "
                              : "text-gray-800 hover:bg-gray-800 hover:text-white",
                            itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                          )
                        }
                        onClick={() => setSelectAll(!selectAll)}
                        value={null}
                      >
                        <div className="w-full flex justify-start items-center">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span
                            className={`inline-block pl-2  text-left truncate ${selectAll ? "font-medium " : "font-normal"}`}
                          >
                            {props?.selectAllTitle ? props?.selectAllTitle : "Select All"}
                          </span>
                        </div>
                      </Combobox.Option>
                    )}

                    {filteredItems?.length === 0
                      ? showDropdownWhenNoData &&
                        showDropdownWhenNoData === true && (
                          <div className="absolute border border-gray-200 shadow-md  rounded-md mt-2 w-full cursor-default select-none py-2 px-4 text-gray-700">
                            {noDataDropDownContent ? (
                              noDataDropDownContent
                            ) : (
                              <p className="text-gray-500 text-sm text-center">No Data ...</p>
                            )}
                          </div>
                        )
                      : filteredItems?.length > 0 &&
                        filteredItems?.map((item: ISelectMenuItemData) => (
                          <Combobox.Option
                            key={item.id}
                            className={({ selected: _l }) =>
                              clsx(
                                "relative cursor-pointer flex border border-t  font-medium text-xs justify-between items-center select-none py-2 text-center ",
                                selectedOptions?.find((so) => so?.id === item.id)
                                  ? "bg-gray-100 hover:bg-gray-100 text-gray-800 font-medium "
                                  : "text-gray-800 hover:bg-gray-800 hover:text-white",
                                itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                              )
                            }
                            data-tooltip-place="right"
                            data-tooltip-id={`option-tooltip:${register?.name}`}
                            data-tooltip-content={item.title}
                            value={item}
                          >
                            {({ selected: _l, active: _isActive }) => (
                              <div className="w-full pl-2 pr-2">
                                <div
                                  className="h-full w-full flex justify-start items-center"
                                  // onClick={() => {
                                  //   const newoptions = selectedOptions.some((p) => p.id == item.id)
                                  //     ? selectedOptions.filter((p) => p.id != item.id)
                                  //     : [...selectedOptions, item];
                                  //   setSelectedOptions(newoptions);
                                  //   if (onSelectItem) onSelectItem(newoptions);
                                  // }}
                                >
                                  {item?.checkboxVisible && item?.checkboxVisible === true ? (
                                    <input
                                      type="checkbox"
                                      checked={isChecked(item) ? true : false}
                                      className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                    />
                                  ) : null}
                                  <span
                                    className={`inline-block pl-2  text-left truncate ${
                                      isChecked(item) ? "font-medium " : "font-normal"
                                    }`}
                                  >
                                    {item.title}
                                  </span>
                                </div>
                                <ul className="mt-2">
                                  {isChecked(item) &&
                                    item?.withSubItems &&
                                    item?.subSelectItems &&
                                    item?.subSelectItems?.length > 0 &&
                                    item?.subSelectItems?.map((subItem) => {
                                      return (
                                        <Combobox.Option
                                          className={
                                            "px-2 py-2 hover:shadow-sm hover:bg-gray-200  w-full flex justify-start items-center"
                                          }
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            handleSelectSubItems(item?.id, subItem);
                                          }}
                                          key={subItem?.id}
                                          value={subItem}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSubChecked(item?.id, subItem) ? true : false}
                                            className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                                          />
                                          <span
                                            className={`inline-block pl-2  text-left truncate ${
                                              isSubChecked(item?.id, subItem) ? "font-medium " : "font-normal"
                                            }`}
                                          >
                                            {subItem.title}
                                          </span>
                                        </Combobox.Option>
                                      );
                                    })}
                                </ul>
                              </div>
                            )}
                          </Combobox.Option>
                        ))}
                  </Combobox.Options>
                </Transition>
              </div>
            </>
          );
        }}
      </Combobox>
      {showTooltips && showTooltips === true && (
        <Tooltip id={`option-tooltip:${register?.name}`} style={{ zIndex: 5, fontSize: "10px" }} />
      )}
    </div>
  );
};

export default ServiceSubServiceSelectMenu;
